import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { type PriceData } from "~/domains/pricing/types/PriceData";
import { type WeightData } from "~/domains/product/types/WeightData";
import { type RootState } from "~/app/store";
import { selectCart } from "~/features/cart/cart/redux/selectors/groupedCartSelectors";
import { selectSalesmanCheckoutCart } from "~/features/cart/cart/redux/selectors/checkoutPageSelectors";
import type { CartGrouping } from "~/features/cart/cart/models/CartData";
import { selectIsPnaLoading } from "~/features/cart/cart/redux/selectors/cartPageSelectors";
import {
  selectActiveQuoteAggregates,
  selectActiveQuoteTotalSum,
  selectActiveQuoteTotalTargetSum,
} from "~/features/cart/activeQuote/redux/selectors";
import {
  blurOfferPrice,
  clearQuote,
  deletePackageEntry,
  toggleCommentVisibility,
  updateComment,
  updateDate,
  updateInternalComment,
  updateOfferPrice,
  updateQuantity,
  updateTargetPrice,
} from "~/features/cart/activeQuote/redux/actions";
import {
  selectPackaging,
  toggleShowPackaging,
  updatePriceReference,
} from "~/features/cart/cart/redux/actions/cartPageActions";
import { copyLastPackageEntry } from "~/features/cart/cart/redux/actions/cartPageActions";
import { selectResolvedGroupedEntry } from "~/features/cart/cart/utils/productEntryHelper";
import { selectSubtotal, selectTotalQty } from "~/features/cart/cart/redux/selectors/productEntrySelectors";
import { CURRENCY_ISO } from "~/utils/constants";
import { cartIsDayDisabled } from "~/common/utils/dateUtils";
import {
  buildQuoteTableViewModel,
  type PerEntrySelectionData,
} from "~/features/cart/activeQuote/utils/buildQuoteTableViewModel";
import type {
  QuotePackageEntryActions,
  QuoteTableViewModel,
} from "~/features/cart/activeQuote/models/QuoteTableViewModel";

export interface ActiveQuoteAggregates {
  requestInFlight: boolean;
  accountNumber: string;
  totalWeight: WeightData;
  totalSum: PriceData;
  totalTargetSum: PriceData;
  entryIds: number[];
  validCart: boolean;
  dstError: boolean;
  showOptimePopupOnCheckout: boolean;
  isPnaLoading: boolean;
}

export interface ActiveQuoteViewModel {
  isLoading: boolean;
  accountNumber: string;
  aggregates: ActiveQuoteAggregates;
  editTableViewModel: QuoteTableViewModel;
  reviewOrderablesViewModel: QuoteTableViewModel;
  reviewQuotablesViewModel: QuoteTableViewModel;
  handleDeleteAll: () => void;
}

export function useActiveQuoteViewModel(): ActiveQuoteViewModel {
  const isLoading = useSelector((state: RootState) => state.cartPage.loading);
  const accountNumber = useSelector((state: RootState) => state.cartPage.accountNumber);
  const baseAggregates = useSelector(selectActiveQuoteAggregates);
  const totalSum = useSelector(selectActiveQuoteTotalSum);
  const totalTargetSum = useSelector(selectActiveQuoteTotalTargetSum);
  const isPnaLoading = useSelector(selectIsPnaLoading);
  const editCartData = useSelector(selectCart);
  const reviewCartData = useSelector(selectSalesmanCheckoutCart);
  const state = useSelector((s: RootState) => s);
  const dispatch = useDispatch();

  const handleDeleteAll = useCallback(() => dispatch(clearQuote()), [dispatch]);

  const buildActions = useCallback(
    (packEntryId: number): QuotePackageEntryActions => ({
      isDayDisabled: (date: Date) => cartIsDayDisabled(state.cart.packEntriesById[packEntryId], date),
      updateDate: (date: Date) => dispatch(updateDate(date, packEntryId)),
      updateQty: (quantity: number) => dispatch(updateQuantity(quantity, packEntryId)),
      updateComment: (comment: string) => dispatch(updateComment(comment, packEntryId)),
      toggleShowComment: () => dispatch(toggleCommentVisibility(packEntryId)),
      updatePriceReference: (priceRef: string | undefined) =>
        dispatch(updatePriceReference(priceRef || "", packEntryId)),
      removeEntry: () => dispatch(deletePackageEntry(packEntryId)),
      onPackagingClick: () => dispatch(toggleShowPackaging(packEntryId)),
      closePackaging: () => dispatch(toggleShowPackaging(packEntryId, false)),
      selectPackage: (packCode: string) => dispatch(selectPackaging(packEntryId, packCode)),
      updateInternalComment: (comment: string) => dispatch(updateInternalComment(comment, packEntryId)),
      updateOfferPrice: (e) =>
        dispatch(
          updateOfferPrice(packEntryId, e ? { value: e.floatValue ?? 0, currencyIso: CURRENCY_ISO } : undefined),
        ),
      blurOfferPrice: () => dispatch(blurOfferPrice(packEntryId)),
      updateTargetPrice: (e) =>
        dispatch(
          updateTargetPrice(packEntryId, e ? { value: e.floatValue ?? 0, currencyIso: CURRENCY_ISO } : undefined),
        ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch],
  );

  const buildCopyEntry = useCallback(
    (resolvedEntry: ReturnType<ReturnType<typeof selectResolvedGroupedEntry>>) => () => {
      const lastPackGroup = resolvedEntry.packGroups[0];
      if (lastPackGroup && lastPackGroup.packEntryIds.length > 0) {
        dispatch(copyLastPackageEntry(lastPackGroup.packEntryIds[lastPackGroup.packEntryIds.length - 1]));
      }
    },
    [dispatch],
  );

  const getDescriptor = useCallback(
    (resolvedEntry: ReturnType<ReturnType<typeof selectResolvedGroupedEntry>>) => resolvedEntry.descriptors[0],
    [],
  );

  const editTableViewModel = useMemo(() => {
    const group = editCartData.orderables;
    const resolvedEntries = group.entries.map((entry) => selectResolvedGroupedEntry(entry)(state));
    const perEntryData = new Map<number, PerEntrySelectionData>();
    resolvedEntries.forEach((resolved) => {
      const productCode = resolved.cartEntry.productCode;
      perEntryData.set(resolved.cartEntryId, {
        totalQuantity: selectTotalQty(state, { productCode }),
        subtotal: selectSubtotal(state, { productCode, isInCheckout: false }),
      });
    });
    return buildQuoteTableViewModel({
      mode: "edit",
      resolvedEntries,
      perEntryData,
      buildActions,
      buildCopyEntry,
      getDescriptor,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, buildActions, buildCopyEntry, getDescriptor]);

  const buildGroupViewModel = useCallback(
    (group: CartGrouping, mode: "edit" | "view") => {
      const resolvedEntries = group.entries.map((entry) => selectResolvedGroupedEntry(entry)(state));
      const perEntryData = new Map<number, PerEntrySelectionData>();
      resolvedEntries.forEach((resolved) => {
        const productCode = resolved.cartEntry.productCode;
        perEntryData.set(resolved.cartEntryId, {
          totalQuantity: selectTotalQty(state, { productCode }),
          subtotal: selectSubtotal(state, { productCode, isInCheckout: false }),
        });
      });
      return buildQuoteTableViewModel({
        mode,
        resolvedEntries,
        perEntryData,
        buildActions,
        buildCopyEntry,
        getDescriptor,
      });
    },

    [state, buildActions, buildCopyEntry, getDescriptor],
  );

  const reviewOrderablesViewModel = useMemo(
    () => buildGroupViewModel(reviewCartData.orderables, "view"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buildGroupViewModel],
  );

  const reviewQuotablesViewModel = useMemo(
    () => buildGroupViewModel(reviewCartData.quotables, "view"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buildGroupViewModel],
  );

  return {
    isLoading,
    accountNumber,
    aggregates: {
      ...baseAggregates,
      totalSum,
      totalTargetSum,
      isPnaLoading,
    },
    editTableViewModel,
    reviewOrderablesViewModel,
    reviewQuotablesViewModel,
    handleDeleteAll,
  };
}
